import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { paymentService } from '../services/api';
import { pixelPurchase } from '../utils/pixel';
import '../styles/App.css';

const CartPage = () => {
  const { items, updateQuantity, getTotalPrice, clearCart, getTotalItems } = useCart();
  const navigate = useNavigate();
  const [customerInfo, setCustomerInfo] = useState({
    customerName: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [paymentFailed, setPaymentFailed] = useState(false);

  const handleAddressChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      address: {
        ...customerInfo.address,
        [e.target.name]: e.target.value
      }
    });
  };
  
  const handleCustomerInfoChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value
    });
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (document.getElementById('razorpay-script')) return resolve(true);
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setPaymentFailed(false);

    try {
      const total = getTotalPrice() + 60;
      const { data } = await paymentService.createOrder(total);

      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Failed to load Razorpay. Check your connection.');

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: 'Sunkissed Pickles',
        description: 'Homemade Pickle Order',
        prefill: {
          name: customerInfo.customerName,
          email: customerInfo.email,
          contact: customerInfo.phone,
        },
        handler: async (response) => {
          try {
            await paymentService.verifyPayment({
              ...response,
              ...customerInfo,
              items,
              totalAmount: total,
            });
            pixelPurchase(items, total);
            clearCart();
            navigate('/order-confirmed', { replace: true });
          } catch {
            setPaymentFailed(true);
            setError('Payment received but order creation failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: () => {
            setPaymentFailed(true);
            setError('Payment was cancelled. You can retry below.');
            setIsSubmitting(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setPaymentFailed(true);
        setError('Payment failed. Please retry.');
        setIsSubmitting(false);
      });
      rzp.open();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to initiate payment.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleRemove = (productId, weight) => {
    updateQuantity(productId, weight, 0);
  };

  if (items.length === 0) {
    return (
      <div className="bg-white min-h-screen">
        <section className="py-12 px-4 md:px-[140px] bg-gradient-to-b from-[#8e0707] to-[#6b0505]">
          <h1 className="text-3xl md:text-5xl font-medium text-white text-center">Your Cart is Empty</h1>
        </section>
        <section className="py-16 px-4 md:px-[140px] text-center">
          <p className="text-[#272727]/70 mb-8">Add some products to your cart to get started.</p>
          <Link to="/products">
            <button className="bg-[#8e0707] hover:bg-[#6b0505] text-white px-8 py-3 rounded">
              Browse Products
            </button>
          </Link>
        </section>
      </div>
    );
  }

  const subtotal = getTotalPrice();
  const shipping = 60;
  const total = subtotal + shipping;
  
  return (
    <div className="bg-[#f8f8f8] min-h-screen">
      <section className="py-8 md:py-12 px-4 md:px-[140px] bg-gradient-to-b from-[#8e0707] to-[#6b0505]">
        <h1 className="text-3xl md:text-5xl font-medium text-white text-center">Cart & Checkout</h1>
      </section>

      <section className="py-8 md:py-16 px-4 md:px-[140px]">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-2xl font-bold text-[#272727] mb-6">Your Items ({getTotalItems()})</h2>
                <div className="space-y-4">
                  {items.map(item => (
                    <div key={`${item.productId}-${item.weight}`} className="flex items-center gap-4">
                      <img src={item.image} alt={item.name} className="w-24 h-24 object-cover rounded-md" />
                      <div className="flex-grow">
                        <h3 className="text-lg font-bold">{item.name}</h3>
                        <p className="text-sm text-gray-500">{item.weight}</p>
                        <p className="text-lg font-bold text-[#8e0707]">₹{item.price}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQuantity(item.productId, item.weight, item.quantity - 1)} className="w-8 h-8 bg-gray-200 rounded-full">-</button>
                        <span>{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.productId, item.weight, item.quantity + 1)} className="w-8 h-8 bg-gray-200 rounded-full">+</button>
                      </div>
                      <button onClick={() => handleRemove(item.productId, item.weight)} className="text-red-500 hover:text-red-700">Remove</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
                <h2 className="text-xl font-bold text-[#272727] mb-6">Order Summary</h2>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>₹{subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shipping</span>
                    <span className={shipping === 0 ? 'text-green-500' : ''}>{shipping === 0 ? 'FREE' : `₹${shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-4 mt-4">
                    <span>Total</span>
                    <span>₹{total.toFixed(2)}</span>
                  </div>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <h2 className="text-xl font-bold text-[#272727] mb-4">Delivery Information</h2>
                  <div>
                    <input type="text" name="customerName" placeholder="Full Name" value={customerInfo.customerName} onChange={handleCustomerInfoChange} required className="w-full px-4 py-2 border rounded"/>
                  </div>
                  <div>
                    <input type="email" name="email" placeholder="Email" value={customerInfo.email} onChange={handleCustomerInfoChange} required className="w-full px-4 py-2 border rounded"/>
                  </div>
                  <div>
                    <input type="tel" name="phone" placeholder="Phone" value={customerInfo.phone} onChange={handleCustomerInfoChange} required className="w-full px-4 py-2 border rounded"/>
                  </div>
                  <div>
                    <textarea name="street" placeholder="Address" value={customerInfo.address.street} onChange={handleAddressChange} required rows={3} className="w-full px-4 py-2 border rounded"/>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><input type="text" name="city" placeholder="City" value={customerInfo.address.city} onChange={handleAddressChange} required className="w-full px-4 py-2 border rounded"/></div>
                    <div><input type="text" name="state" placeholder="State" value={customerInfo.address.state} onChange={handleAddressChange} required className="w-full px-4 py-2 border rounded"/></div>
                  </div>
                  <div><input type="text" name="zipCode" placeholder="Pincode" value={customerInfo.address.zipCode} onChange={handleAddressChange} required className="w-full px-4 py-2 border rounded"/></div>

                  <button type="submit" disabled={isSubmitting} className="w-full bg-[#8e0707] hover:bg-[#6b0505] text-white py-3 rounded mt-4">
                    {isSubmitting ? 'Processing...' : `Pay ₹${total.toFixed(2)}`}
                  </button>
                  {error && <p className="text-red-500 mt-4">{error}</p>}
                  {paymentFailed && (
                    <button
                      type="submit"
                      className="w-full border border-[#8e0707] text-[#8e0707] py-3 rounded mt-2 hover:bg-[#8e0707] hover:text-white"
                    >
                      Retry Payment
                    </button>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CartPage;
